import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const RFPDocumentSelectionModal = ({
  isOpen,
  onClose,
  onSelect,
  selectedIds = [],
  proposalDocuments = []
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [localSelectedIds, setLocalSelectedIds] = useState(selectedIds);

  useEffect(() => {
    setLocalSelectedIds(selectedIds);
  }, [selectedIds, isOpen]);

  if (!isOpen) return null;

  const filteredDocs = proposalDocuments
    .filter(doc =>
      doc.original_file_name?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) =>
      (a.original_file_name || '').localeCompare(b.original_file_name || '')
    );

  const handleToggle = (docId) => {
    if (localSelectedIds.includes(docId)) {
      setLocalSelectedIds(localSelectedIds.filter(id => id !== docId));
    } else {
      setLocalSelectedIds([...localSelectedIds, docId]);
    }
  };

  const handleConfirm = () => {
    onSelect(localSelectedIds);
    onClose();
  };

  const handleCancel = () => {
    setLocalSelectedIds(selectedIds);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              Select RFP Documents
            </h3>
            <p className="text-sm text-slate-600 mt-1">
              Choose documents to reference in your AI prompt
            </p>
          </div>
          <button
            onClick={handleCancel}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <FontAwesomeIcon icon="times" className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className="px-6 pt-4">
          <div className="relative">
            <FontAwesomeIcon
              icon="search"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4"
            />
            <input
              type="text"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Document List */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {filteredDocs.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <FontAwesomeIcon icon="file-alt" className="text-4xl mb-3 opacity-50" />
              <p>No RFP documents found</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredDocs.map((doc) => {
                const isSelected = localSelectedIds.includes(doc.document_id);
                return (
                  <div
                    key={doc.document_id}
                    onClick={() => handleToggle(doc.document_id)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      isSelected
                        ? 'border-green-500 bg-green-50'
                        : 'border-slate-200 hover:border-green-300 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-start">
                      <div className="flex-shrink-0 mt-1">
                        <div
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            isSelected
                              ? 'bg-green-500 border-green-500'
                              : 'border-slate-300'
                          }`}
                        >
                          {isSelected && (
                            <FontAwesomeIcon
                              icon="check"
                              className="text-white w-3 h-3"
                            />
                          )}
                        </div>
                      </div>
                      <div className="ml-3 flex-1">
                        <div className="flex items-center">
                          <FontAwesomeIcon
                            icon="file-alt"
                            className="text-green-600 mr-2"
                          />
                          <span className="font-medium text-slate-900">
                            {doc.original_file_name}
                          </span>
                        </div>
                        <div className="mt-2 text-sm text-slate-600">
                          <div className="flex items-center space-x-4">
                            <span>
                              <FontAwesomeIcon icon="file" className="mr-1 text-slate-400" />
                              {doc.file_type?.toUpperCase() || 'Document'}
                            </span>
                            {doc.upload_date && (
                              <span>
                                <FontAwesomeIcon icon="calendar" className="mr-1 text-slate-400" />
                                {new Date(doc.upload_date).toLocaleDateString()}
                              </span>
                            )}
                            {doc.file_size && (
                              <span>
                                <FontAwesomeIcon icon="database" className="mr-1 text-slate-400" />
                                {(doc.file_size / 1024).toFixed(0)} KB
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50">
          <div className="text-sm text-slate-600">
            {localSelectedIds.length} document{localSelectedIds.length !== 1 ? 's' : ''} selected
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleCancel}
              className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Confirm Selection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RFPDocumentSelectionModal;
